
import {
    Component,
    ElementRef,
    EventEmitter,
    Input,
    OnChanges,
    OnInit,
    Output,
    SimpleChanges,
    TemplateRef,
    ViewChild,
    ViewContainerRef,
    ChangeDetectorRef
} from '@angular/core';
import { IQuotationCost, IQuotationMargin, IQuotationRetail } from "libraries/service/resources/quotation-quotations/IQuotationsQuotation";
import { CalculationMode } from "libraries/interfaces/IWenji";
import { faAngleUp, faAngleDown, IconDefinition, faBullseye } from "@fortawesome/free-solid-svg-icons";

@Component({
    selector: 'td-percent',
    template: `
        <ng-template #template>
            <td mat-cell
                class="percent"
                #cell
                [attr.colspan]="colspan"
                (keyup.enter)="focusField()"
                (click)="focusField()"
                [class.is-editable]="isEditable"
                [class.oddElement]="parity === 'odd'"
                [class.evenElement]="parity === 'even'"
                [class.visual-focus]="navigationFocus"
                [class.has-collapser]="collapser"
                [class.has-header]="header"
                [ngStyle]="{'left':left}">
                <span class="header-cell" *ngIf="header">
                    {{header}}
                </span>
                <div class="container-td">
                    <span *ngIf="!hasPercent && !isSimplePercent">-</span>
                    <span *ngIf="hasPercent">{{percent.used | percent:'1.0-2':'fr'}}</span>
                    <span *ngIf="isSimplePercent">
                        <ng-container *ngIf="!hasSimplePercent">-</ng-container>
                        <ng-container *ngIf="hasSimplePercent">{{(simplePercent / 100) | percent:'1.0-2':'fr'}}</ng-container>
                    </span>
                    <span *ngIf="collapser">
                        <fa-icon [icon]="iconAngleDown" class="collapser" *ngIf="!isCollapsed"></fa-icon>
                        <fa-icon [icon]="iconAngleUp"   class="collapser" *ngIf="isCollapsed"></fa-icon>
                    </span>
                </div>
                <span class="footer" *ngIf="hasDifferentReference">
                    <fa-icon [icon]="iconBullseye" ></fa-icon> {{percent.reference | percent:'1.0-2':'fr'}}
                </span>
                <div class="menu-container">
                    <ng-content></ng-content>
                </div>
                <input type="text"
                       #focuser
                       (focus)="setEdit()"
                       class="focuser"
                       *ngIf="(price || isSimplePercent) && isEditable">
            </td>
        </ng-template>
    `,
    styleUrls: ["./td-percent.component.scss"],
})

export class TdPercentComponent implements OnInit, OnChanges {

    @Input()
    public colspan: number = 1;
    @Input()
    public price: IQuotationCost | IQuotationRetail;
    public originalPrice: IQuotationCost | IQuotationRetail;
    @Input()
    public isEditable: boolean;
    @Input()
    public isSticky: boolean;
    @Input()
    public parity: "odd" | "even" = "odd";
    @Input()
    public calculationMode: CalculationMode = CalculationMode.margin;
    @Input()
    public collapser: boolean;
    @Input()
    public forceFocus: boolean;
    @Input()
    public navigationFocus: boolean;
    @Input()
    public canEmitRightPosition: boolean;
    @Input()
    public simplePercent: number;
    @Input()
    public header: string;
    @Input()
    public isSimplePercent: boolean;
    public hasSimplePercent: boolean;
    @Input()
    public fixedWidth: string;

    @Input()
    get isCollapsed(): boolean {
        return this.isCollapsedValue;
    }

    set isCollapsed(state: boolean) {
        this.isCollapsedValue = state;
        this.isCollapsedChange.emit(this.isCollapsedValue);
    }

    @Output()
    public readonly isCollapsedChange: EventEmitter<boolean> = new EventEmitter<boolean>();
    @Output()
    public readonly onClick: EventEmitter<boolean> = new EventEmitter<boolean>();
    @Output()
    public readonly onFocus: EventEmitter<boolean> = new EventEmitter<boolean>();
    @Output()
    public readonly onPercentChange: EventEmitter<number> = new EventEmitter<number>();

    public percent: IQuotationMargin;
    public hasPercent: boolean;
    public hasDifferentReference: boolean;
    public left: string;
    public style: { [key: string]: string } = {};

    public isBlurOnInput: boolean = false;

    public iconAngleUp: IconDefinition = faAngleUp;

    public iconAngleDown: IconDefinition = faAngleDown;

    public iconBullseye: IconDefinition = faBullseye;

    @ViewChild('cell')
    private readonly cell: ElementRef;

    @ViewChild('template', {read: TemplateRef, static: true})
    private readonly template: TemplateRef<{}>;

    @ViewChild('focuser')
    private readonly focuser: ElementRef;

    private isCollapsedValue: boolean;

    constructor(
        private readonly viewContainerRef: ViewContainerRef,
        private readonly changeDetectorRef: ChangeDetectorRef
    ) {
    }

    public ngOnInit(): void {
        this.viewContainerRef.createEmbeddedView(this.template);
        setTimeout(() => {
            this.stickCell();
        });
        if(this.fixedWidth){
            this.style['min-width'] = this.fixedWidth;
            this.style.width = this.fixedWidth;
        }
    }

    public ngOnChanges(changes: SimpleChanges): void {
        if (this.forceFocus && this.focuser) {
            this.focuser.nativeElement.focus();

            // Avoids the error ExpressionChangedAfterItHasBeenCheckedError
            // as the focus output is then used to update templates
            this.changeDetectorRef.detectChanges();
        } else if (this.focuser) {
            this.focuser.nativeElement.blur();
            this.changeDetectorRef.detectChanges();
        }

        if (changes &&
            (
                (changes.calculationMode && changes.calculationMode.currentValue) ||
                (changes.price && changes.price.currentValue)
            )
        ) {
            this.initDisplay();
        }
        if (changes && changes.simplePercent) {
            this.initSimplePercentDisplay();
        }
    }

    public focusField(): void {
        if ((this.price || this.isSimplePercent) && this.isEditable && this.focuser) {
            this.focuser.nativeElement.focus();
        }
    }

    public setEdit(): void {
        if (this.collapser) {
            this.isCollapsed = !this.isCollapsed;
        }
        this.originalPrice = {...this.price};
        this.onFocus.emit(true);
        this.isBlurOnInput = false;
    }

    private stickCell(): void {
        if (this.isSticky) {
            const left: number =
                this.cell.nativeElement.offsetLeft;
            this.cell.nativeElement.style.zIndex = "1";
            this.left = `${left}px`;
            this.cell.nativeElement.style.position = "sticky";
            this.changePercent();
        }
    }

    private initDisplay(): void {
        if (this.price) {
            this.percent = this.price[this.calculationMode];
            this.hasPercent = Boolean(this.percent) && Boolean(this.percent.used || this.percent.used === 0);
            this.hasDifferentReference = this.hasPercent ? Boolean(this.percent.used !== this.percent.reference) : this.hasPercent;
            setTimeout(() => this.changePercent());
        }
    }

    private initSimplePercentDisplay(): void {
        if (this.isSimplePercent) {
            this.hasSimplePercent = Boolean(this.simplePercent || this.simplePercent === 0);
            setTimeout(() => this.changePercent());
        }
    }

    private changePercent(): void {
        if (this.canEmitRightPosition && this.cell) {
            const left: number = this.cell.nativeElement.offsetLeft - this.cell.nativeElement.parentElement.offsetParent.offsetLeft;
            this.onPercentChange.emit(left + this.cell.nativeElement.offsetWidth);
        }
    }
}
